import { HQ, sportsimPath } from "../sportConfig";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SimulationTable from "./SimulationTable";
import "./TipPage.css";

async function get(path, options) {
  const response = await fetch(sportsimPath(path), options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || "Kunde inte hämta simuleringen.");
  return body;
}

export default function MatchSimulation({ simulation, standings, match }) {
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search}`;
  const [fixtures, setFixtures] = useState([]);
  const [saved, setSaved] = useState({});
  const localKey = `hockey-hq-sim-${simulation?.key}`;
  const [drafts, setDrafts] = useState(() => {
    if (HQ.sport !== "hockey") return {};
    try { return JSON.parse(localStorage.getItem(localKey)) || {}; } catch { return {}; }
  });
  const [account, setAccount] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!simulation) return;
    let active = true;
    setLoading(true);
    Promise.all([get(`/matches?simulation=${encodeURIComponent(simulation.key)}`), get("/account")])
      .then(async ([rows, user]) => {
        const tips = user.authenticated ? await get(`/predictions?simulation=${encodeURIComponent(simulation.key)}`) : [];
        if (active) { setFixtures(rows); setAccount(user.authenticated); setSaved(Object.fromEntries(tips.map((tip) => [tip.match_id, tip]))); setLoading(false); }
      })
      .catch((err) => { if (active) { setMessage(err.message); setLoading(false); } });
    return () => { active = false; };
  }, [simulation]);

  const upcoming = useMemo(() => fixtures.filter((row) => row.can_tip), [fixtures]);
  const selectedMatchId = match?.id;
  const selectedFixture = fixtures.find((row) => String(row.match_id) === String(selectedMatchId));
  const roundNo = selectedFixture?.round_no || upcoming[0]?.round_no;
  const visible = useMemo(() => fixtures.filter((row) => row.round_no === roundNo).sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || "")), [fixtures, roundNo]);
  const saveableDrafts = Object.entries(drafts).filter(([id, tip]) => fixtures.find((row) => row.id === Number(id))?.can_tip && tip.home !== "" && tip.away !== "");
  const predictions = Object.fromEntries(fixtures.map((row) => {
    const draft = drafts[row.id];
    return [row.id, draft ? { pred_home: draft.home, pred_away: draft.away, pred_result_type: draft.type } : saved[row.id]];
  }));

  const saveLocal = () => { localStorage.setItem(localKey, JSON.stringify(drafts)); setMessage("Simuleringen är sparad på den här enheten."); };

  const change = (id, side, value) => {
    setMessage("");
    setDrafts((current) => ({ ...current, [id]: {
      home: current[id]?.home ?? (saved[id]?.pred_home != null ? String(saved[id].pred_home) : String(match?.id === String(fixtures.find((row) => row.id === id)?.match_id) && match?.status_id < 4 ? match?.home_goals ?? "" : "")),
      away: current[id]?.away ?? (saved[id]?.pred_away != null ? String(saved[id].pred_away) : String(match?.id === String(fixtures.find((row) => row.id === id)?.match_id) && match?.status_id < 4 ? match?.away_goals ?? "" : "")),
      type: current[id]?.type ?? saved[id]?.pred_result_type ?? "",
      [side]: value,
    } }));
  };

  const save = async () => {
    const rows = saveableDrafts
      .map(([id, tip]) => ({ match_id: Number(id), pred_home: Number(tip.home), pred_away: Number(tip.away), pred_result_type: tip.type || null }));
    if (!rows.length) { setMessage("Fyll i båda resultaten först."); return; }
    setBusy(true);
    try {
      await get("/predictions", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ simulation: simulation.key, predictions: rows }) });
      const tips = await get(`/predictions?simulation=${encodeURIComponent(simulation.key)}`);
      setSaved(Object.fromEntries(tips.map((tip) => [tip.match_id, tip])));
      setDrafts({});
      setMessage("Tipsen är sparade.");
    } catch (err) { setMessage(err.message); } finally { setBusy(false); }
  };

  if (!simulation) return <div className="events-state">Simulering finns inte för den här serien.</div>;
  if (loading) return <div className="events-state">Hämtar simuleringen…</div>;
  return <section className="match-simulation">
    <div className="match-simulation-heading"><h2>{simulation.name}{roundNo ? ` · omgång ${roundNo}` : ""}</h2><Link to={`/tips?serie=${encodeURIComponent(simulation.key)}&from=${encodeURIComponent(returnPath)}`}>Alla matcher ↗</Link></div>
    {message && <p className="tips-notice" role="status">{message}</p>}
    {visible.map((row) => {
      const current = row.id === selectedFixture?.id;
      const live = current && !row.can_tip && match?.status_id > 1 && match?.status_id < 4;
      const finished = current && !row.can_tip && match?.status_id === 4;
      const editable = row.can_tip || live;
      const homeValue = drafts[row.id]?.home ?? saved[row.id]?.pred_home ?? (current ? match?.home_goals : row.home_goals) ?? "";
      const awayValue = drafts[row.id]?.away ?? saved[row.id]?.pred_away ?? (current ? match?.away_goals : row.away_goals) ?? "";
      return <div className={`match-simulation-fixture${current ? " match-simulation-fixture-current" : ""}`} key={row.id}>
        {(current || !row.can_tip) && <small className="match-simulation-badge">{live ? "Pågår · prova slutresultat" : finished ? "Matchen är slut" : current ? "Den här matchen" : row.home_goals != null ? "Spelad" : "Startad"}</small>}
        <span>{row.home_team}</span><div className="tips-score"><input aria-label={`${row.home_team} mål`} type="number" min="0" max="99" inputMode="numeric" placeholder="–" disabled={!editable} value={homeValue} onChange={(event) => change(row.id, "home", event.target.value)} /><span>:</span><input aria-label={`${row.away_team} mål`} type="number" min="0" max="99" inputMode="numeric" placeholder="–" disabled={!editable} value={awayValue} onChange={(event) => change(row.id, "away", event.target.value)} /></div><span>{row.away_team}</span>
        {HQ.sport === "hockey" && <select aria-label={`Avgörande för ${row.home_team} mot ${row.away_team}`} disabled={!editable} value={drafts[row.id]?.type ?? saved[row.id]?.pred_result_type ?? ""} onChange={event => change(row.id, "type", event.target.value)}><option value="">Ordinarie</option><option value="ef">Förlängning</option><option value="es">Straffar</option></select>}
        {live && <small className="match-simulation-note">Prognosen för den här matchen räknas i tabellen men kan inte sparas som tips.</small>}
      </div>;
    })}
    {!visible.length && <p className="tips-empty">Inga kommande matcher finns i den här serien.</p>}
    <SimulationTable dividerRanks={simulation?.divider_ranks} standings={standings?.standings} matches={fixtures} predictions={predictions} rules={simulation.point_rules || {}} currentTeams={[match?.home_team, match?.away_team]} />
    <div className="match-simulation-actions">{HQ.sport === "hockey" && <button onClick={saveLocal}>Spara simulering</button>}{account ? <button className="tips-primary" disabled={busy || !saveableDrafts.length} onClick={save}>{busy ? "Sparar…" : "Spara kommande tips"}</button> : <Link to={`/tips?serie=${encodeURIComponent(simulation.key)}&from=${encodeURIComponent(returnPath)}`}>Logga in för att spara ↗</Link>}<a href={`/sportsim/?simulation=${encodeURIComponent(simulation.key)}`} target="_blank" rel="noopener noreferrer">Fler ligor hos Sportsim ↗</a></div>
  </section>;
}
